import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Inbox, RefreshCw, CheckCircle2, XCircle, Trash2, Mail, Phone, Building2,
  MapPin, AtSign, MessageSquare, Loader2, ClipboardCopy, AlertTriangle,
  Search, FileText, HelpCircle, ScrollText, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { govAuth, GOV_API } from '../../lib/govApi';
import { toast } from '../../hooks/use-toast';

const STATUS_FILTERS = [
  { v: 'new', l: 'Pending', badge: 'bg-amber-400 text-amber-900' },
  { v: 'awaiting_info', l: 'Awaiting Info', badge: 'bg-orange-400 text-orange-900' },
  { v: 'approved', l: 'Approved', badge: 'bg-emerald-500 text-white' },
  { v: 'rejected', l: 'Rejected', badge: 'bg-rose-500 text-white' },
  { v: 'all', l: 'All', badge: 'bg-slate-400 text-white' },
];

const STATUS_BADGE = {
  new: 'bg-amber-100 text-amber-800 border-amber-200',
  awaiting_info: 'bg-orange-100 text-orange-800 border-orange-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

const CLASSIFICATION_STYLE = {
  government: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  educational: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  corporate: 'bg-blue-100 text-blue-800 border-blue-200',
  public_provider: 'bg-amber-100 text-amber-800 border-amber-200',
  unknown: 'bg-slate-100 text-slate-700 border-slate-200',
};

const GovAccessRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [search, setSearch] = useState('');
  const [acting, setActing] = useState(null);           // request being approved/rejected/info-requested
  const [actionType, setActionType] = useState('');      // 'approve' | 'reject' | 'request_info'
  const [reviewNotes, setReviewNotes] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastApproval, setLastApproval] = useState(null);
  const [auditFor, setAuditFor] = useState(null);        // request whose audit trail is open
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${GOV_API}/admin/access-requests`, govAuth());
      setRequests(res.data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const counts = useMemo(() => {
    const c = { new: 0, awaiting_info: 0, approved: 0, rejected: 0, all: requests.length };
    requests.forEach(r => { c[r.status || 'new'] = (c[r.status || 'new'] || 0) + 1; });
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter(r => {
      if (filter !== 'all' && (r.status || 'new') !== filter) return false;
      if (!q) return true;
      return [r.request_ref, r.agency_name, r.contact_name, r.contact_email, r.agency_type, r.email_domain]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [requests, filter, search]);

  const openAction = (req, type) => {
    setActing(req);
    setActionType(type);
    setReviewNotes('');
    setAdminPassword('');
    setLastApproval(null);
  };
  const closeAction = () => { setActing(null); setActionType(''); setLastApproval(null); };

  const submitAction = async () => {
    if (!acting) return;
    setSubmitting(true);
    try {
      if (actionType === 'approve') {
        const res = await axios.post(
          `${GOV_API}/admin/access-requests/${acting.id}/approve`,
          { review_notes: reviewNotes || undefined, admin_password: adminPassword || undefined },
          govAuth(),
        );
        setLastApproval(res.data);
        toast({ title: 'Approved & provisioned', description: `Welcome email sent to ${res.data.user_email}` });
        await fetchAll();
      } else if (actionType === 'reject') {
        if (!reviewNotes.trim()) {
          toast({ title: 'Reason required', description: 'Please give the requester a brief reason.', variant: 'destructive' });
          setSubmitting(false);
          return;
        }
        await axios.post(`${GOV_API}/admin/access-requests/${acting.id}/reject`,
          { reason: reviewNotes }, govAuth());
        toast({ title: 'Request rejected', description: 'Notification email sent.' });
        await fetchAll();
        closeAction();
      } else if (actionType === 'request_info') {
        if (!reviewNotes.trim()) {
          toast({ title: 'Comments required', description: 'Tell the applicant exactly what you need.', variant: 'destructive' });
          setSubmitting(false);
          return;
        }
        await axios.post(`${GOV_API}/admin/access-requests/${acting.id}/request-info`,
          { comments: reviewNotes }, govAuth());
        toast({ title: 'Additional information requested', description: 'Applicant has been emailed.' });
        await fetchAll();
        closeAction();
      }
    } catch (e) {
      toast({ title: 'Action failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (req) => {
    if (!window.confirm(`Permanently delete ${req.request_ref} from ${req.agency_name}?`)) return;
    try {
      await axios.delete(`${GOV_API}/admin/access-requests/${req.id}`, govAuth());
      toast({ title: 'Deleted' });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const openAudit = async (req) => {
    setAuditFor(req);
    setAuditRows([]);
    setAuditLoading(true);
    try {
      const res = await axios.get(`${GOV_API}/admin/access-requests/${req.id}/audit`, govAuth());
      setAuditRows(res.data || []);
    } catch (e) {
      toast({ title: 'Audit fetch failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="gov-access-requests-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Inbox className="h-7 w-7 text-blue-700" /> Agency Onboarding
            {counts.new > 0 && <span className="ml-2 bg-amber-400 text-amber-900 text-xs font-bold rounded-full px-2 py-0.5">{counts.new} pending</span>}
          </h1>
          <p className="text-sm text-slate-500">Review, approve, reject, or request more information on new agency access requests.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ID, org, email…"
                   className="pl-7 w-64" data-testid="access-search" />
          </div>
          <Button variant="outline" onClick={fetchAll} data-testid="access-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {STATUS_FILTERS.map(s => (
          <button key={s.v} onClick={() => setFilter(s.v)}
            className={`text-left px-3 py-2 rounded-lg border-2 transition ${filter === s.v ? 'border-blue-700 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
            data-testid={`kpi-${s.v}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{s.l}</div>
            <div className="text-2xl font-bold text-slate-800">{counts[s.v] || 0}</div>
          </button>
        ))}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          {STATUS_FILTERS.map(s => (
            <TabsTrigger key={s.v} value={s.v} data-testid={`filter-${s.v}`}>
              {s.l}
              {counts[s.v] > 0 && <span className={`ml-1 text-[10px] font-bold rounded px-1 ${s.badge}`}>{counts[s.v]}</span>}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>
      ) : visible.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-10 text-center text-slate-500">
            <Inbox className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            No {filter === 'all' ? '' : filter.replace('_', ' ')} requests{search && ` matching "${search}"`}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map(r => {
            const dc = r.domain_classification || { status: 'unknown', label: '—', icon: '⚠', advisory: '' };
            const status = r.status || 'new';
            return (
              <Card key={r.id} className="border-2" data-testid={`access-request-${r.id}`}>
                <CardContent className="p-4">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <code className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200 font-mono">{r.request_ref || '—'}</code>
                      <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-700" /> {r.agency_name}
                      </h3>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{r.agency_type || 'state_agency'}</span>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${STATUS_BADGE[status] || STATUS_BADGE.new}`}>
                      {status.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Body — 2 columns on md+ */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-600">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /> <a href={`mailto:${r.contact_email}`} className="text-blue-700 underline">{r.contact_email}</a></div>
                      <div className="flex items-center gap-2 text-slate-500"><span className="w-3.5" /> {r.contact_name} <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">Agency Admin</span></div>
                      {r.contact_phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {r.contact_phone}</div>}
                    </div>
                    <div className="space-y-1">
                      {(r.org_address || r.org_city) && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                          <span>
                            {r.org_address && <>{r.org_address}<br /></>}
                            {[r.org_city, r.org_state, r.org_zip].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      {r.email_domain && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <AtSign className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-mono text-[11px]">@{r.email_domain}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CLASSIFICATION_STYLE[dc.status]}`}>
                            {dc.icon} {dc.label}
                          </span>
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400">Submitted {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</div>
                    </div>
                  </div>

                  {r.notes && <div className="text-xs text-slate-600 mt-2 flex items-start gap-2 pt-2 border-t border-slate-100"><MessageSquare className="h-3.5 w-3.5 text-slate-400 mt-0.5" /> <span className="italic">{r.notes}</span></div>}
                  {r.review_notes && <div className="text-xs text-slate-600 mt-2 flex items-start gap-2 pt-2 border-t border-slate-100"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5" /> <span><strong>Admin comment:</strong> {r.review_notes}</span></div>}
                  {r.agency_id && <div className="text-[10px] text-emerald-700 mt-2">✓ Provisioned · agency_id <code>{r.agency_id}</code></div>}

                  {/* Action bar */}
                  <div className="flex flex-wrap gap-2 pt-3 mt-2 border-t border-slate-100">
                    {(status === 'new' || status === 'awaiting_info') && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openAction(r, 'approve')} data-testid={`approve-${r.id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => openAction(r, 'request_info')} data-testid={`request-info-${r.id}`}>
                          <HelpCircle className="h-4 w-4 mr-1" /> Request More Info
                        </Button>
                        <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => openAction(r, 'reject')} data-testid={`reject-${r.id}`}>
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => openAudit(r)} data-testid={`audit-${r.id}`}>
                      <ScrollText className="h-3.5 w-3.5 mr-1" /> Audit trail
                    </Button>
                    <Button size="sm" variant="ghost" className="text-slate-400 ml-auto" onClick={() => onDelete(r)} data-testid={`delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Action dialog */}
      {acting && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeAction}>
          <Card className="w-full max-w-lg bg-white" onClick={(e) => e.stopPropagation()} data-testid="access-action-dialog">
            <CardHeader>
              <CardTitle className="text-base">
                {actionType === 'approve' && <>Approve &amp; provision · <code className="text-xs">{acting.request_ref}</code></>}
                {actionType === 'reject' && <>Reject · <code className="text-xs">{acting.request_ref}</code></>}
                {actionType === 'request_info' && <>Request more info · <code className="text-xs">{acting.request_ref}</code></>}
              </CardTitle>
              <p className="text-xs text-slate-500">{acting.agency_name}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {lastApproval ? (
                <div className="space-y-2">
                  <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700 inline mr-1" />
                    <strong>Provisioned successfully.</strong>
                  </div>
                  <div className="text-sm space-y-1">
                    <div><strong>Agency ID:</strong> <code>{lastApproval.agency_id}</code></div>
                    <div><strong>Agency Admin email:</strong> {lastApproval.user_email}</div>
                    <div className="text-slate-500">
                      {lastApproval.temp_password_sent
                        ? '✉ Welcome email with temporary password sent (HTML + plain text).'
                        : 'ℹ Existing user account linked. No password email sent.'}
                    </div>
                  </div>
                  <Button onClick={() => copyJSON(lastApproval)} variant="outline" size="sm">
                    <ClipboardCopy className="h-3.5 w-3.5 mr-1" /> Copy details
                  </Button>
                  <div className="flex justify-end pt-2">
                    <Button onClick={closeAction} data-testid="access-action-done">Done</Button>
                  </div>
                </div>
              ) : (
                <>
                  {actionType === 'approve' && (
                    <div className="text-xs text-slate-600 p-2 bg-blue-50 border border-blue-200 rounded space-y-1">
                      <p>This will:</p>
                      <ul className="list-disc list-inside text-[11px] space-y-0.5">
                        <li>Create the <strong>organisation</strong> record (type &quot;{acting.agency_type || 'state_agency'}&quot;)</li>
                        <li>Create an initial <strong>Agency Admin</strong> for <code>{acting.contact_email}</code></li>
                        <li>Send a welcome email with sign-in URL &amp; temporary password (auto-generated unless you set one)</li>
                        <li>User is forced to change the password on first sign-in</li>
                      </ul>
                    </div>
                  )}
                  {actionType === 'reject' && (
                    <div className="text-xs text-slate-600 p-2 bg-rose-50 border border-rose-200 rounded">
                      The contact will be emailed your reason for rejection. They can reply directly to follow up.
                    </div>
                  )}
                  {actionType === 'request_info' && (
                    <div className="text-xs text-slate-600 p-2 bg-orange-50 border border-orange-200 rounded">
                      The applicant will be emailed with your comments. The request moves to the <strong>Awaiting Info</strong> queue.
                    </div>
                  )}

                  <div>
                    <Label htmlFor="notes">
                      {actionType === 'approve' && 'Welcome note (optional)'}
                      {actionType === 'reject' && 'Reason (required)'}
                      {actionType === 'request_info' && 'What do you need from the applicant? (required)'}
                    </Label>
                    <Textarea id="notes" rows={3} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} data-testid="access-action-notes"
                      placeholder={
                        actionType === 'approve' ? 'e.g. Your purchasing tier has been set to Net-30…' :
                        actionType === 'reject' ? 'e.g. Out of contract scope. Please contact sales for a custom quote.' :
                        'e.g. Please provide official government letterhead confirming your role as Agency Administrator.'
                      } />
                  </div>
                  {actionType === 'approve' && (
                    <div>
                      <Label htmlFor="pw">Temporary password (optional — auto-generated if blank)</Label>
                      <Input id="pw" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="leave blank to auto-generate" data-testid="access-action-password" />
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={closeAction}>Cancel</Button>
                    <Button
                      onClick={submitAction}
                      disabled={submitting}
                      className={
                        actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                        actionType === 'reject' ? 'bg-rose-600 hover:bg-rose-700 text-white' :
                        'bg-orange-600 hover:bg-orange-700 text-white'
                      }
                      data-testid="access-action-submit-btn"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : (
                        actionType === 'approve' ? <CheckCircle2 className="h-4 w-4 mr-1" /> :
                        actionType === 'reject' ? <XCircle className="h-4 w-4 mr-1" /> :
                        <HelpCircle className="h-4 w-4 mr-1" />
                      )}
                      {actionType === 'approve' ? 'Approve & provision' :
                       actionType === 'reject' ? 'Reject' : 'Send request'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit trail modal */}
      {auditFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAuditFor(null)}>
          <Card className="w-full max-w-2xl bg-white max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="audit-trail-dialog">
            <CardHeader className="border-b pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ScrollText className="h-4 w-4 text-blue-700" />
                Audit trail · <code className="text-xs">{auditFor.request_ref}</code>
              </CardTitle>
              <p className="text-xs text-slate-500">{auditFor.agency_name}</p>
            </CardHeader>
            <CardContent className="overflow-y-auto p-0">
              {auditLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
              ) : auditRows.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">No audit entries.</div>
              ) : (
                <div className="divide-y">
                  {auditRows.map((e, i) => (
                    <div key={i} className="p-3 flex items-start gap-3 hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                      <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono font-semibold text-slate-800">{e.event}</span>
                          <span className="text-[11px] text-slate-500">by <strong>{e.actor}</strong></span>
                          {e.ip && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-mono">{e.ip}</span>}
                          <span className="text-[10px] text-slate-400 ml-auto">{new Date(e.at).toLocaleString()}</span>
                        </div>
                        {e.meta && Object.keys(e.meta).length > 0 && (
                          <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(e.meta, null, 2)}</pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="border-t p-3 flex justify-end">
              <Button variant="outline" onClick={() => setAuditFor(null)}>Close</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

function copyJSON(obj) {
  navigator.clipboard.writeText(JSON.stringify(obj, null, 2)).then(
    () => toast({ title: 'Copied to clipboard' }),
    () => toast({ title: 'Copy failed', variant: 'destructive' }),
  );
}

export default GovAccessRequests;
